import type { NodeComponentType } from '@amarantos/api-client'
import { getListNodeComponentTypesQueryOptions, useListNodeComponentTypesSuspense } from '@amarantos/api-client'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useCallback, useDeferredValue } from 'react'
import { useTranslation } from 'react-i18next'
import { pick } from 'remeda'
import { ConfirmDeleteDialog } from '@/components/confirm-delete-dialog'
import { DeleteButton } from '@/components/delete-button'
import { PageContent, PageHeader, PageTitle } from '@/components/page'
import { PaginationBar } from '@/components/pagination-bar'
import { RouteErrorFallback } from '@/components/route-error-fallback'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
	NodeComponentTypeFormDrawer,
	type NodeComponentTypeFormDrawerState,
} from '@/features/node-component-types/components/node-component-type-form-drawer'
import { NodeComponentTypesFilters } from '@/features/node-component-types/components/node-component-types-filters'
import { NodeComponentTypesTable } from '@/features/node-component-types/components/node-component-types-table'
import { useDeleteNodeComponentTypeMutation } from '@/features/node-component-types/hooks/use-node-component-type-mutations'
import {
	nodeComponentTypesSearchSchema,
	toListNodeComponentTypesParams,
} from '@/features/node-component-types/utils/search'
import { useConfirmAction } from '@/hooks/use-confirm-action'
import { useDebouncedInput } from '@/hooks/use-debounced-input'
import { getApiErrorMessage } from '@/utils/errors'

export const Route = createFileRoute('/_authenticated/node-component-types')({
	validateSearch: nodeComponentTypesSearchSchema,
	loaderDeps: ({ search }) => search,
	loader: ({ context: { queryClient }, deps }) => {
		queryClient.ensureQueryData(getListNodeComponentTypesQueryOptions(toListNodeComponentTypesParams(deps)))
	},
	errorComponent: ({ error, reset }) => (
		<RouteErrorFallback error={error} reset={reset} fallbackMessageKey='error.load_node_component_types' />
	),
	component: NodeComponentTypesRoute,
})

function NodeComponentTypesRoute() {
	const { t } = useTranslation()
	const search = Route.useSearch()
	const navigate = Route.useNavigate()
	const deferredSearch = useDeferredValue(search)
	const isStale = deferredSearch !== search

	const nodeComponentTypesQuery = useListNodeComponentTypesSuspense(toListNodeComponentTypesParams(deferredSearch))

	const deleteNodeComponentType = useConfirmAction<NodeComponentType, { id: NodeComponentType['id'] }>({
		mutation: useDeleteNodeComponentTypeMutation(),
		toVariables: (nodeComponentType) => ({ id: nodeComponentType.id }),
		formatError: (err) => getApiErrorMessage(err, t('error.delete_node_component_type')),
	})

	const nodeComponentTypes = nodeComponentTypesQuery.data?.data ?? []
	const meta = nodeComponentTypesQuery.data?.meta
	const currentPage = search.page
	const totalPages = meta?.total_pages ?? 1
	const drawerState: NodeComponentTypeFormDrawerState = pick(search, ['mode', 'nodeComponentTypeId'])
	const drawerOpen = drawerState.mode !== undefined

	const onSearchChange = useCallback(
		(nextValue: string) => {
			navigate({
				replace: true,
				search: (previous) => ({
					...previous,
					search: nextValue,
					page: 1,
				}),
			})
		},
		[navigate],
	)

	const searchInputProps = useDebouncedInput({
		value: search.search,
		onDebouncedChange: onSearchChange,
	})

	return (
		<>
			<PageHeader breadcrumbs={[{ label: t('nav.node_component_types') }]}>
				<PageTitle
					subtitle={t('node_component_types.page.description')}
					actions={
						<Button asChild>
							<Link
								to='.'
								search={(previous) => ({ ...previous, mode: 'create' as const, nodeComponentTypeId: undefined })}
							>
								{t('node_component_types.create.button')}
							</Link>
						</Button>
					}
				>
					{t('nav.node_component_types')}
				</PageTitle>
			</PageHeader>
			<PageContent>
				<Card>
					<CardContent className='space-y-4'>
						<div className='flex flex-col gap-2 sm:flex-row sm:items-center'>
							<Input
								{...searchInputProps}
								placeholder={t('node_component_types.search.placeholder')}
								className='max-w-sm'
							/>
							<NodeComponentTypesFilters
								onApply={() => {
									navigate({ search: (previous) => ({ ...previous, page: 1 }) })
								}}
							/>
						</div>

						<NodeComponentTypesTable
							nodeComponentTypes={nodeComponentTypes}
							isStale={isStale}
							actions={(nodeComponentType) => (
								<>
									<Button asChild variant='outline' size='sm'>
										<Link
											to='.'
											search={(previous) => ({
												...previous,
												mode: 'edit' as const,
												nodeComponentTypeId: nodeComponentType.id,
											})}
										>
											{t('node_component_types.edit.button')}
										</Link>
									</Button>
									<DeleteButton onClick={() => deleteNodeComponentType.request(nodeComponentType)} />
								</>
							)}
						/>

						<PaginationBar
							currentPage={currentPage}
							totalPages={totalPages}
							onPageChange={(page) => navigate({ search: (previous) => ({ ...previous, page }) })}
						/>
					</CardContent>
				</Card>

				<NodeComponentTypeFormDrawer
					{...drawerState}
					open={drawerOpen}
					onOpenChange={(isOpen) => {
						if (!isOpen) {
							navigate({
								search: (previous) => ({
									...previous,
									mode: undefined,
									nodeComponentTypeId: undefined,
								}),
							})
						}
					}}
				/>

				<ConfirmDeleteDialog
					{...deleteNodeComponentType.dialogProps}
					title={t('node_component_types.delete.title')}
					description={t('node_component_types.delete.description', {
						name: deleteNodeComponentType.target?.name ?? t('node_component_types.delete.fallback_name'),
					})}
				/>
			</PageContent>
		</>
	)
}
